import { useEffect } from 'react';

export default function PrivacyPolicy() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Privacy Policy | RoundJump';
    return () => { document.title = previousTitle; };
  }, []);

  return (
    <main className="privacy-page">
      <article className="privacy-card">
        <header className="privacy-header">
          <a className="privacy-brand" href="/">
            <img className="brand-mark" src="/brand/roundjump-logo.webp" alt="" />
            <span>RoundJump</span>
          </a>
          <a className="privacy-back" href="/">Back to sign up</a>
        </header>

        <p className="eyebrow">Effective August 1, 2026</p>
        <h1>Privacy Policy</h1>
        <p className="privacy-lead">RoundJump provides private, AI-assisted interview practice. This policy explains what information we collect, why we use it, where it goes, and the choices you have.</p>

        <section>
          <h2>Information we collect</h2>
          <p><strong>Account information.</strong> When you create an account, we receive your email address and account identifier. If you use a third-party sign-in provider, we may also receive your name and profile photo.</p>
          <p><strong>Interview content.</strong> We process the role, interview type, difficulty, answers, transcripts, scores, and feedback associated with your practice sessions.</p>
          <p><strong>Files and repositories.</strong> If you choose to personalize an interview, we process files such as resumes, job descriptions, and notes, or selected content from a public code repository.</p>
          <p><strong>Voice and avatar sessions.</strong> We process microphone audio to transcribe and conduct the interview. Voice and avatar sessions may be recorded as mixed audio and stored with the session.</p>
          <p><strong>Technical information.</strong> Our infrastructure and service providers may log IP addresses, browser or device details, request metadata, errors, and security events needed to operate and protect the service.</p>
        </section>

        <section>
          <h2>How we use information</h2>
          <p>We use this information to authenticate you, run and personalize interviews, generate transcripts and feedback, preserve your practice history, process account deletion, prevent abuse, troubleshoot failures, and improve the reliability of RoundJump.</p>
          <p>We do not sell your personal information or use your interview content for targeted advertising.</p>
        </section>

        <section>
          <h2>Service providers</h2>
          <p>We use service providers for account authentication; cloud hosting, databases, and private storage; AI model processing; speech recognition and synthesis; public repository retrieval; and network connectivity. Depending on the features you use, these providers may process account identifiers, interview prompts and answers, selected interview context, audio, transcripts, or technical logs as needed to operate RoundJump.</p>
          <p>These providers process data under their own terms and may operate in countries outside yours.</p>
        </section>

        <section>
          <h2>Storage and retention</h2>
          <p>Account records, interview sessions, transcripts, uploaded context, and recordings are retained while your account is active so you can use your history and context again. Generated speech cache files are scheduled for deletion after 30 days.</p>
          <p>When you request account deletion, RoundJump queues deletion of your live authentication identity, database records, uploaded files, transcripts, and recordings. Encrypted database backups, recovery logs, and soft-deleted storage copies may remain for limited recovery windows before expiring. Operational event records may retain identifiers needed to document processing and diagnose failures.</p>
        </section>

        <section>
          <h2>Your choices</h2>
          <p>You decide whether to upload files, add a repository, or use microphone-based practice. You can request deletion from the account controls in the app. To request access, correction, or help with deletion, contact us at <a href="mailto:hello@roundjump.com">hello@roundjump.com</a>.</p>
        </section>

        <section>
          <h2>Security</h2>
          <p>We use authenticated access, encrypted connections, private cloud storage, service identities, and access controls designed to protect your information. No online service can guarantee absolute security.</p>
        </section>

        <section>
          <h2>Children</h2>
          <p>RoundJump is not directed to children under 16, and we do not knowingly collect their personal information. Contact us if you believe a child has provided information to the service.</p>
        </section>

        <section>
          <h2>Changes and contact</h2>
          <p>We may update this policy as the service changes. We will revise the effective date and provide additional notice when a change materially affects how we use personal information.</p>
          <p>RoundJump is the operator responsible for this policy. Questions can be sent to <a href="mailto:hello@roundjump.com">hello@roundjump.com</a>.</p>
        </section>
      </article>
    </main>
  );
}
